import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    ChevronRight,
    Eye,
    ClipboardEdit,
    BarChart,
    ShieldAlert,
    Search,
    Users,
    Activity,
    FileSpreadsheet
} from 'lucide-react';
import './SafetyObservation.css';

const SafetyObservation = () => {
    useEffect(() => {
        document.title = "Safety Observation & Incident Prevention | X-Max";
        let metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            metaDesc.setAttribute('content', 'Enhance workplace safety with X-Max Safety Observation. Fostering a zero-accident culture through proactive identification and automated safety workflows.');
        }
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="safety-page">
            {/* Hero Section */}
            <section className="safety-hero">
                <div className="container safety-hero-container">
                    <div className="safety-hero-content">
                        <div className="sec-tag">CORE MODULES</div>
                        <h1>Safety Observation: <span className="gradient-text">Proactive Incident Prevention</span></h1>
                        <p className="hero-desc">
                            X-Max's Safety Observation module transforms workplace safety by enabling continuous, proactive identification and reporting of unsafe acts and conditions.
                        </p>
                        <p className="hero-subtext">
                            Moving beyond traditional reactive safety measures, it fosters a culture where <span className="highlight-text">every team member is empowered</span> to contribute to a safer environment, preventing incidents before they occur.
                        </p>
                        <Link to="/book-now" className="btn btn-primary btn-lg">
                            Book Demo <ChevronRight size={20} />
                        </Link>
                    </div>
                    <div className="safety-hero-visual">
                        <div className="visual-wrapper">
                            <img
                                src="./assets/modules/safety.png"
                                alt="Industrial Safety Monitoring"
                                className="hero-img"
                            />
                            <div className="visual-overlay"></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Workflow Section */}
            <section className="safety-workflow">
                <div className="container">
                    <div className="section-header">
                        <div className="sec-tag">METHODOLOGY</div>
                        <h2>Safety Observation <span className="gradient-text">Workflow</span></h2>
                        <p>X-Max incorporates the renowned DuPont STOP (Safety Training Observation Program) methodology to embed proactive
                            safety into daily operations. This module empowers supervisors and employees to conduct structured safety observations,
                            fostering a culture of continuous improvement and hazard elimination by focusing on behaviours and conditions before
                            incidents occur.</p>
                    </div>

                    <div className="workflow-steps">
                        <div className="workflow-card">
                            <div className="workflow-icon"><Search size={24} /></div>
                            <h3>Plan & Observe</h3>
                            <p>Systematically observe work tasks, identifying critical steps and potential at-risk behaviours or unsafe conditions.</p>
                            <div className="workflow-arrow"><ChevronRight size={20} /></div>
                        </div>
                        <div className="workflow-card">
                            <div className="workflow-icon"><Users size={24} /></div>
                            <h3>Engage & Discuss</h3>
                            <p>Initiate constructive conversations with workers to reinforce safe practices and collaboratively identify areas for improvement.</p>
                            <div className="workflow-arrow"><ChevronRight size={20} /></div>
                        </div>
                        <div className="workflow-card">
                            <div className="workflow-icon"><Activity size={24} /></div>
                            <h3>Act & Correct</h3>
                            <p>Implement immediate corrective actions for any identified at-risk behaviours or conditions to prevent potential incidents.</p>
                            <div className="workflow-arrow"><ChevronRight size={20} /></div>
                        </div>
                        <div className="workflow-card">
                            <div className="workflow-icon"><FileSpreadsheet size={24} /></div>
                            <h3>Document & Analyse</h3>
                            <p>Record all observations within X-Max for trending, root cause analysis, and to inform targeted training and preventive measures.</p>
                        </div>
                    </div>
                    <p className="mt-20">By digitising this critical workflow, X-Max ensures that every observation contributes to a robust database, allowing for
                        powerful analytics to identify trends, pinpoint high-risk areas, and drive sustainable safety improvements across your facility.</p>
                </div>
            </section>

            {/* Core Pillars Section */}
            <section className="safety-pillars">
                <div className="container">
                    <div className="pillars-grid">
                        <div className="pillar-box">
                            <div className="pillar-header">
                                <Eye className="pillar-icon" />
                                <h3>Observe</h3>
                            </div>
                            <p>Employees can easily observe and identify potential hazards or unsafe behaviours using a mobile app.</p>
                        </div>
                        <div className="pillar-box">
                            <div className="pillar-header">
                                <ClipboardEdit className="pillar-icon" />
                                <h3>Report</h3>
                            </div>
                            <p>Observations are immediately reported, including details, photos, and location, ensuring swift communication.</p>
                        </div>
                        <div className="pillar-box">
                            <div className="pillar-header">
                                <BarChart className="pillar-icon" />
                                <h3>Analyze</h3>
                            </div>
                            <p>The reported data is then analyzed to identify trends and root causes.</p>
                        </div>
                        <div className="pillar-box">
                            <div className="pillar-header">
                                <ShieldAlert className="pillar-icon" />
                                <h3>Prevent</h3>
                            </div>
                            <p>Targeted corrective actions and preventive measures significantly reduce recurrence and foster a zero-accident culture.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer CTA */}
            <section className="safety-footer-cta">
                <div className="container">
                    <div className="cta-card">
                        <h2>Build a resilient, zero-accident culture today.</h2>
                        <Link to="/book-now" className="btn btn-primary btn-lg">Request Safety Demo</Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default SafetyObservation;
